/**
 * L'onglet « Offres de cours » : ce que `<TutorOffersPage>` montre, lu dans le compte (patron A : les
 * listes et les compteurs sont rangés dans `currentUser` par ses cas SUCCESS — `getGroupAttributionProposals`
 * selon `is_trial`, `getSubstitutionProposals`, `getProposalsCount`).
 *
 * Port de `ProposalsTabScreen.js` : les onglets que les droits ouvrent, chacun avec son compte en
 * attente ; l'onglet demandé, ou le premier quand il n'est pas ouvert à ce tuteur ; les offres de
 * l'onglet dans l'ordre du serveur (ciblées en tête — la liste est paginée, un tri ici mélangerait
 * les pages) ; le rappel « Confirme tes créneaux » (`availabilityNudgeFromState`).
 *
 * Un `createSelector` sur les tranches du compte qu'il lit et sur l'onglet demandé.
 */
import { createSelector } from "reselect"

import { tutorAvailabilityNudge, type ApiTutorTasks } from "../../../adapters/tutor/Home/tutorTodoAdapter"
import {
    type ApiTutorOffersUser,
    toUITutorGroupOffer,
    toUITutorSubstitutionOffer,
    tutorOfferTabs,
} from "../../../adapters/tutor/shared/tutorOfferAdapter"
import { type UITutorOffer, type UITutorOfferKind, type UITutorOfferTab } from "../../../ui/types/tutorDispoOffres"
import { selectAccount } from "../../shared/displayedStudent"

export type TutorOffersViewModel = {
    tabs: UITutorOfferTab[]
    /** L'onglet montré : celui demandé s'il est ouvert, sinon le premier. */
    activeKind: UITutorOfferKind
    offers: UITutorOffer[]
    dispoStale: boolean
    /** Le compte n'est pas encore là. */
    loading: boolean
}

type Account =
    | (ApiTutorOffersUser & {
          profile?: (ApiTutorOffersUser["profile"] & { availability_freshness?: string | null }) | null
      })
    | null

type OffersState = { data?: { tutors?: { tasks?: ApiTutorTasks | null } | null } }

const account = (state: unknown) => selectAccount(state as never) as Account

const isOffer = (offer: UITutorOffer | null): offer is UITutorOffer => offer != null

const selectOffers = createSelector(
    [
        (state: unknown) => account(state)?.profile ?? null,
        (state: unknown) => account(state)?.group_attribution_proposals ?? null,
        (state: unknown) => account(state)?.trial_attribution_proposals ?? null,
        (state: unknown) => account(state)?.substitution_proposals ?? null,
        (state: unknown) => account(state)?.group_attribution_proposals_meta ?? null,
        (state: unknown) => account(state)?.trial_attribution_proposals_meta ?? null,
        (state: unknown) => account(state)?.substitution_proposals_meta ?? null,
        (state: unknown) => (state as OffersState)?.data?.tutors?.tasks ?? null,
        (state: unknown) => account(state) == null,
        (_state: unknown, requested: UITutorOfferKind | null) => requested,
    ],
    (profile, groups, trials, substitutions, groupMeta, trialMeta, substitutionMeta, tasks, noAccount, requested) => {
        const user: ApiTutorOffersUser = {
            profile,
            group_attribution_proposals_meta: groupMeta,
            trial_attribution_proposals_meta: trialMeta,
            substitution_proposals_meta: substitutionMeta,
        }
        const tabs = tutorOfferTabs(user)
        const activeKind =
            requested != null && tabs.some((tab) => tab.kind === requested) ? requested : (tabs[0]?.kind ?? "group")
        const rowsOf = <T>(rows: T[] | null) => (Array.isArray(rows) ? rows : [])
        const offers =
            activeKind === "substitution"
                ? rowsOf(substitutions).map(toUITutorSubstitutionOffer)
                : rowsOf(activeKind === "trial" ? trials : groups).map((row) => toUITutorGroupOffer(row, activeKind))
        return {
            tabs,
            activeKind,
            offers: offers.filter(isOffer),
            dispoStale: tutorAvailabilityNudge(tasks, profile?.availability_freshness),
            loading: noAccount,
        } satisfies TutorOffersViewModel
    }
)

export function tutorOffersViewModel(state: unknown, requested: UITutorOfferKind | null): TutorOffersViewModel {
    return selectOffers(state, requested)
}
